import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useI18n } from "@/i18n";
import { api } from "@/lib/api";

const BOX = "w-full rounded-full py-2.5 text-sm font-semibold inline-flex justify-center";

function ChangePlanButton({ p, up, remaining, onDone }) {
  const { t, errMsg } = useI18n();
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await api.post("/billing/change-plan", { plan_id: p.id }); toast.success(t("plans.changed")); onDone(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button disabled={busy} className={`${BOX} ${up ? "btn-brand" : "border border-neutral-300 text-neutral-800 hover:border-neutral-500"}`} data-testid={`plan-change-${p.id}`}>
          {busy ? <Loader2 size={16} className="animate-spin" /> : t(up ? "plans.upgrade" : "plans.downgrade")}
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent className="bg-[var(--bg-1)] border-[var(--line)]">
        <AlertDialogHeader>
          <AlertDialogTitle>{t(up ? "plans.upgrade" : "plans.downgrade")} — {p.name}</AlertDialogTitle>
          <AlertDialogDescription data-testid="plan-change-text">{t(up ? "plans.upgradeConfirm" : "plans.downgradeConfirm", { name: p.name, n: up ? remaining : p.credits_per_month })}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="plan-change-cancel">{t("common.no")}</AlertDialogCancel>
          <AlertDialogAction onClick={go} className="bg-emerald-600 hover:bg-emerald-700" data-testid="plan-change-confirm">{t("common.yes")}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function PlanAction({ p, price, interval, ent, curOrder, chosen, onChoose, reloadEnt }) {
  const { t } = useI18n();
  const sub = ent?.subscription;
  const payable = p.payable?.[interval];
  const muted = (txt) => <span className={`${BOX} text-neutral-400 text-xs`} data-testid={`plan-unavailable-${p.id}`}>{txt}</span>;
  if (ent?.active && sub) {
    if (p.id === (sub.plan_id || "baslangic")) return <span className={`${BOX} bg-emerald-50 text-emerald-700`} data-testid={`plan-current-${p.id}`}>{t("plans.current")}</span>;
    if (!sub.provider_ref || sub.status !== "active") return muted(t("plans.manualShort"));
    if (!payable) return muted(t("plans.unavailable"));
    return <ChangePlanButton p={p} up={p.sort_order > curOrder} remaining={ent.remaining} onDone={reloadEnt} />;
  }
  if (!price) return <span className="block h-10" data-testid={`plan-unavailable-${p.id}`} />;
  if (!payable) return muted(t("plans.unavailable"));
  const on = chosen?.id === p.id;
  return (
    <button type="button" onClick={() => onChoose(p)} data-testid={`plan-choose-${p.id}`}
      className={`${BOX} ${on ? "btn-brand" : "border border-neutral-300 text-neutral-800 hover:border-emerald-600"}`}>{t(on ? "plans.selected" : "plans.choose")}</button>
  );
}
